import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import { blocks, i18next, site, view, dialogs } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import segment from 'src/js/modules/analytics/segment';
import contextMenu from 'src/js/modules/context-menu';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import tabIdentifiers from 'src/js/modules/website-settings/constants/tab-identifiers';
import isTextInBlockChanged from '../verifiers/is-text-in-block-changed';

const { ActionButton, Popover, PopoverContent, PopoverTrigger } = designSystem;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const POPOVER_WIDTH = 310;
const POPOVER_HEIGHT = 150;

const getCaptions = (hasBusinessInfoInWebsiteSettings, openWebsiteSettings) => ({
  title: i18next.t('Generate block'),
  buttonLabel: i18next.t('Generate'),
  description: (
    <>
      {i18next.t('Generate your block based on your needs and business profile.')}&nbsp;
      {hasBusinessInfoInWebsiteSettings ? (
        <>
          {i18next.t('Customize it any time under {link}', { link: '' })}
          <a onClick={openWebsiteSettings} onTouchStart={openWebsiteSettings} href="/">
            {i18next.t('Settings')} &#x2192; {i18next.t('Business Info')}
          </a>
        </>
      ) : null}
    </>
  ),
});

// Height of one trigger in the vertical control pane (px)
const VERTICAL_TRIGGER_SIZE = 40;
// Gap between generate button and the first trigger (px)
const VERTICAL_TRIGGER_GAP = 8;

function BlockGenerationTriggerContainer({
  element,
  offsetX,
  offsetY,
  viewportHeight,
  hasBusinessInfoInWebsiteSettings = false,
  isVertical = false,
}) {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);

  const [labelShown, setLabelShown] = useState(true);

  const businessCategory = useSelector(site.selectors.getBusinessCategory);
  const scrollPosition = useSelector(view.selectors.getScrollPosition);

  const blockId = blocks.accessors.getBlockIdByElement(element);
  const variationId = blocks.accessors.getVariationIdByElement(element);
  const blockCategory =
    blocks.accessors.getBlockCategoryByElement(element) ||
    blocks.accessors.getBlockCategory(blockId, variationId);

  const openWebsiteSettings = useCallback(
    (e) => {
      e.preventDefault();

      dispatch(contextMenu.actions.hideContextMenu());
      dispatch(
        dialogs.actions.show(dialogTypes.WEBSITE_SETTINGS, {
          activeTabId: tabIdentifiers.BUSINESS_INFO,
        })
      );
    },
    [dispatch]
  );

  const captions = useMemo(
    () => getCaptions(hasBusinessInfoInWebsiteSettings, openWebsiteSettings),
    [hasBusinessInfoInWebsiteSettings, openWebsiteSettings]
  );

  const handleGenerateClick = () => {
    trackEvent(events.AI_BLOCK_GENERATE_BUTTON_CLICKED, {
      businessCategory,
      blockId,
      blockVariationId: variationId,
      categoryId: blockCategory,
      isTextChanged: isTextInBlockChanged(element),
    });

    dispatch(view.actions.setHoveredElement(null));
    dispatch(view.actions.setSelectedElement(null));
    dispatch(
      dialogs.actions.show(dialogTypes.BLOCK_GENERATION_DIALOG, {
        element,
        captions,
        initialBlockCategory: blockCategory,
      })
    );
  };

  const handleOrientationChange = useCallback((e) => {
    const portrait = e.matches;

    setLabelShown(!portrait);
  }, []);

  useEffect(() => {
    // Hide button label on mobile devices with portrait orientation
    if (!bowser.mobile) return undefined;

    const mediaQueryList = window.matchMedia('(orientation: portrait)');

    mediaQueryList.addEventListener('change', handleOrientationChange);
    handleOrientationChange(mediaQueryList);

    return () => {
      mediaQueryList.removeEventListener('change', handleOrientationChange);
    };
  }, [handleOrientationChange]);

  const popoverPlacement =
    scrollPosition + viewportHeight - offsetY > POPOVER_HEIGHT ? 'bottom-center' : 'top-center';

  const top = isVertical ? offsetY - VERTICAL_TRIGGER_SIZE - VERTICAL_TRIGGER_GAP : offsetY;

  return (
    <div
      className={`ws-generate-block-wrapper${
        isVertical ? ' ws-generate-block-wrapper--vertical' : ''
      }`}
      style={{
        left: offsetX,
        top,
      }}
      ref={wrapperRef}
    >
      <Popover
        disabled={bowser.mobile}
        placement={popoverPlacement}
        width={POPOVER_WIDTH}
        title={captions.title}
        description={captions.description}
      >
        <PopoverTrigger>
          <div className="ws-generate-block-wrapper__button">
            <ActionButton
              size="medium"
              format="solid"
              appearance="accent"
              label={!isVertical && labelShown ? captions.buttonLabel : null}
              iconGlyph="ai"
              onClick={handleGenerateClick}
            />
          </div>
        </PopoverTrigger>
        <PopoverContent />
      </Popover>
    </div>
  );
}

BlockGenerationTriggerContainer.propTypes = {
  element: PropTypes.object.isRequired,
  offsetX: PropTypes.number.isRequired,
  offsetY: PropTypes.number.isRequired,
  viewportHeight: PropTypes.number.isRequired,
  hasBusinessInfoInWebsiteSettings: PropTypes.bool,
  isVertical: PropTypes.bool,
};

export default BlockGenerationTriggerContainer;
